import React, { useState, useEffect } from "react";
import { TabContent, TabPane, Offcanvas, OffcanvasHeader, OffcanvasBody } from "reactstrap";
import { NeoInput, NeoTabs } from "../../../../../components/Common";
import WorkoutSessionPanel from "./WorkoutSessionPanel";
import { DEFAULT_SET_BY_TYPE } from "../../constants";
import SwimSetLibraryPicker from "../swimming/SwimSetLibraryPicker";
import StrengthExerciseLibraryPicker from "../strength/StrengthExerciseLibraryPicker";
import WodBenchmarkLibraryPicker from "../wod/WodBenchmarkLibraryPicker";
import RunningSessionLibraryPicker from "../running/RunningSessionLibraryPicker";
import RunningHelpPanel from "../running/RunningHelpPanel";
import { buildStrengthWorkoutSetFromExercise } from "../../domain/strength/exerciseLibrary";
import { DEFAULT_RUNNING_ATTENDANCE_PLAN, DEFAULT_RUNNING_CLUB_SESSION, DEFAULT_RUNNING_CYCLE_PLAN } from "../../domain/running/constants";
import { calculateRunningWorkoutSummary } from "../../domain/running/calculations";
import { buildRunningWatchExport, downloadRunningWatchExport } from "../../domain/running/watchExport";
import { getScoreTypeForWodFormat } from "../../domain/wod/constants";
import { toast } from "react-toastify";
import { useTenant } from "../../../../../hooks/useTenant";
import RunningSessionLibraryService from "../../../../../services/running_session_library_service";
import Swal from "sweetalert2";

const WorkoutTabsEditor = ({ values, setFieldValue, handleChange }) => {
    const { idTenant, user: authUser } = useTenant();
    const [activeTab, setActiveTab] = useState(0);
    const [targetBlockBySession, setTargetBlockBySession] = useState({});
    const [libraryRefreshKey, setLibraryRefreshKey] = useState(0);
    const [librarySidebarOpen, setLibrarySidebarOpen] = useState(false);
    const [strengthLibrarySidebarOpen, setStrengthLibrarySidebarOpen] = useState(false);
    const [wodLibrarySidebarOpen, setWodLibrarySidebarOpen] = useState(false);
    const [runningLibrarySidebarOpen, setRunningLibrarySidebarOpen] = useState(false);
    const [runningHelpSidebarOpen, setRunningHelpSidebarOpen] = useState(false);
    const [runningCoachBriefOpen, setRunningCoachBriefOpen] = useState({});
    const [runningConfigTab, setRunningConfigTab] = useState({});
    const defaultWorkoutType = values.workoutType || "strength";

    // ── Sincroniza abas com frequência semanal ──
    useEffect(() => {
        const freq = values.weeklyFrequency || 1;
        let currentWorkouts = values.sessions || values.workouts || [];

        let shouldUpdate = false;

        if (currentWorkouts.length !== freq) {
            const newWorkouts = [...currentWorkouts];
            if (newWorkouts.length < freq) {
                for (let i = newWorkouts.length; i < freq; i++) {
                    const char = String.fromCharCode(65 + i);
                    newWorkouts.push({
                        id: Date.now().toString() + i,
                        name: `Treino ${char}`,
                        sessionType: defaultWorkoutType,
                        clubSession: defaultWorkoutType === "running" ? DEFAULT_RUNNING_CLUB_SESSION : undefined,
                        cyclePlan: defaultWorkoutType === "running" ? DEFAULT_RUNNING_CYCLE_PLAN : undefined,
                        attendancePlan: defaultWorkoutType === "running" ? DEFAULT_RUNNING_ATTENDANCE_PLAN : undefined,
                        blocks: [{
                            id: Date.now().toString() + "_blk_" + i,
                            name: defaultWorkoutType === "wod" ? "WOD" : "Bloco 1",
                            workoutType: defaultWorkoutType,
                            method: defaultWorkoutType === "strength" ? "normal" : "",
                            blockType: defaultWorkoutType === "running" ? "main" : "",
                            repeat: defaultWorkoutType === "running" ? 1 : "",
                            restBetweenRepeats: "",
                            sessionType: defaultWorkoutType === "running" ? "easy" : "",
                            locationType: defaultWorkoutType === "running" ? "street" : "",
                            terrain: defaultWorkoutType === "running" ? "flat" : "",
                            componentType: defaultWorkoutType === "wod" ? "metcon" : "",
                            format: defaultWorkoutType === "wod" ? "amrap" : "",
                            scoreType: defaultWorkoutType === "wod" ? getScoreTypeForWodFormat("amrap") : "",
                            timeCap: defaultWorkoutType === "wod" ? "20 min" : "",
                            rounds: "",
                            stimulus: "",
                            scalingNotes: "",
                            sets: [{
                                id: Date.now().toString() + "_set_" + i,
                                type: defaultWorkoutType,
                                ...DEFAULT_SET_BY_TYPE[defaultWorkoutType]
                            }]
                        }]
                    });
                }
            } else {
                newWorkouts.length = freq;
            }
            currentWorkouts = newWorkouts;
            shouldUpdate = true;
        }

        if (shouldUpdate) {
            setFieldValue("sessions", currentWorkouts);
        }

        if (activeTab >= freq) {
            setActiveTab(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.weeklyFrequency, values.sessions?.length, defaultWorkoutType, setFieldValue]);

    const workouts = values.sessions || [];

    if (workouts.length === 0) return null;

    const showTabs = workouts.length > 1;

    // Tipo de treino da aba ativa (p/ decidir qual Offcanvas exibir)
    const activeWorkout = workouts[activeTab] || {};
    const activeSessionType = activeWorkout.sessionType || activeWorkout.workoutType || defaultWorkoutType;

    const isSwimming = activeSessionType === "swimming";
    const isStrength = activeSessionType === "strength";
    const isWod = activeSessionType === "wod";
    const isRunning = activeSessionType === "running";

    // ── Handlers de adição de itens de biblioteca ──

    const handleAddSwimSet = (sessionIndex, set) => {
        const session = workouts[sessionIndex] || {};
        const blocks = session.blocks || [];
        const safeBlockIndex = Number.isInteger(targetBlockBySession[sessionIndex])
            ? targetBlockBySession[sessionIndex]
            : 0;

        if (blocks.length === 0) {
            setFieldValue(`sessions.${sessionIndex}.blocks`, [{
                id: Date.now().toString(),
                name: "Bloco 1",
                workoutType: "swimming",
                method: "",
                sets: [{ id: Date.now().toString(), ...set }],
            }]);
            return;
        }

        const blockIndex = Math.min(safeBlockIndex, blocks.length - 1);
        const currentSets = blocks[blockIndex]?.sets || [];
        setFieldValue(`sessions.${sessionIndex}.blocks.${blockIndex}.sets`, [
            ...currentSets,
            { id: Date.now().toString(), ...set },
        ]);
    };

    const handleAddStrengthExercise = (sessionIndex, exercise) => {
        const session = workouts[sessionIndex] || {};
        const blocks = session.blocks || [];
        const safeBlockIndex = Number.isInteger(targetBlockBySession[sessionIndex])
            ? targetBlockBySession[sessionIndex]
            : 0;

        const defaultSet = buildStrengthWorkoutSetFromExercise(exercise, {
            id: Date.now().toString(),
        });

        if (blocks.length === 0) {
            setFieldValue(`sessions.${sessionIndex}.blocks`, [{
                id: Date.now().toString(),
                name: "Bloco 1",
                workoutType: "strength",
                method: "normal",
                sets: [defaultSet],
            }]);
            return;
        }

        const blockIndex = Math.min(safeBlockIndex, blocks.length - 1);
        const currentSets = blocks[blockIndex]?.sets || [];
        setFieldValue(`sessions.${sessionIndex}.blocks.${blockIndex}.sets`, [
            ...currentSets,
            defaultSet,
        ]);
    };

    const handleAddWodBenchmark = (sessionIndex, benchmark) => {
        const blocks = (benchmark.blocks || []).map((block, blockIndex) => ({
            ...block,
            id: `${Date.now()}_wod_block_${blockIndex}`,
            sets: (block.sets || []).map((set, setIndex) => ({
                ...set,
                id: `${Date.now()}_wod_set_${blockIndex}_${setIndex}`,
            })),
        }));

        if (blocks.length === 0) return;

        setFieldValue(`sessions.${sessionIndex}.blocks`, blocks);
        setFieldValue(`sessions.${sessionIndex}.name`, benchmark.name || `Treino ${sessionIndex + 1}`);
    };

    const handleAddRunningSession = (sessionIndex, session) => {
        const blocks = (session.blocks || []).map((block, blockIndex) => ({
            ...block,
            id: `${Date.now()}_run_block_${blockIndex}`,
            workoutType: "running",
            sets: (block.sets || []).map((set, setIndex) => ({
                ...set,
                id: `${Date.now()}_run_segment_${blockIndex}_${setIndex}`,
            })),
        }));

        if (blocks.length === 0) return;

        setFieldValue(`sessions.${sessionIndex}.blocks`, blocks);
        setFieldValue(`sessions.${sessionIndex}.name`, session.name || `Treino ${sessionIndex + 1}`);
        setFieldValue(`sessions.${sessionIndex}.clubSession`, session.clubSession || workouts[sessionIndex]?.clubSession || DEFAULT_RUNNING_CLUB_SESSION);
        setFieldValue(`sessions.${sessionIndex}.cyclePlan`, session.cyclePlan || workouts[sessionIndex]?.cyclePlan || DEFAULT_RUNNING_CYCLE_PLAN);
        setFieldValue(`sessions.${sessionIndex}.attendancePlan`, session.attendancePlan || workouts[sessionIndex]?.attendancePlan || DEFAULT_RUNNING_ATTENDANCE_PLAN);
    };

    const handleSaveRunningWorkoutSession = async (sessionIndex, workout) => {
        const summary = calculateRunningWorkoutSummary(workout);
        const defaultName = workout.name || `Sessão ${sessionIndex + 1}`;
        const defaultTags = [
            workout.clubSession?.isClubSession ? "clube" : "individual",
            workout.clubSession?.teamName,
            workout.blocks?.[0]?.sessionType,
        ].filter(Boolean).join(", ");

        const { value: formValues } = await Swal.fire({
            title: "Salvar sessão completa",
            width: 720,
            html: `
                <div class="row g-3 text-start">
                    <div class="col-md-7">
                        <label class="form-label font-size-12 fw-bold text-muted mb-1">Nome da sessão</label>
                        <input id="run-save-name" class="form-control" placeholder="Ex: Clube - 6x400m" value="${defaultName}">
                    </div>
                    <div class="col-md-5">
                        <label class="form-label font-size-12 fw-bold text-muted mb-1">Grupo da biblioteca</label>
                        <select id="run-save-group" class="form-control">
                            <option value="club" selected>Clube</option>
                            <option value="interval">Intervalados</option>
                            <option value="base">Base</option>
                            <option value="threshold">Limiar/Tempo</option>
                            <option value="long_run">Longões</option>
                            <option value="hill">Subidas</option>
                            <option value="return">Retorno</option>
                        </select>
                    </div>
                    <div class="col-md-4">
                        <label class="form-label font-size-12 fw-bold text-muted mb-1">Objetivo</label>
                        <input id="run-save-objective" class="form-control" placeholder="5K, 10K, Base" value="${values.objective || ""}">
                    </div>
                    <div class="col-md-4">
                        <label class="form-label font-size-12 fw-bold text-muted mb-1">Nível</label>
                        <input id="run-save-level" class="form-control" placeholder="Todos, intermediário..." value="${values.level || ""}">
                    </div>
                    <div class="col-md-2">
                        <label class="form-label font-size-12 fw-bold text-muted mb-1">Km</label>
                        <input id="run-save-distance" class="form-control" value="${summary.distanceKm ? summary.distanceKm.toFixed(1) : ""}">
                    </div>
                    <div class="col-md-2">
                        <label class="form-label font-size-12 fw-bold text-muted mb-1">Min</label>
                        <input id="run-save-duration" class="form-control" value="${summary.durationMin ? Math.round(summary.durationMin) : ""}">
                    </div>
                    <div class="col-12">
                        <label class="form-label font-size-12 fw-bold text-muted mb-1">Tags</label>
                        <input id="run-save-tags" class="form-control" placeholder="clube, pista, 5k" value="${defaultTags}">
                    </div>
                    <div class="col-12">
                        <label class="form-label font-size-12 fw-bold text-muted mb-1">Observações</label>
                        <textarea id="run-save-notes" class="form-control" rows="2" placeholder="Foco técnico, logística, segurança ou objetivo.">${workout.clubSession?.safetyNotes || ""}</textarea>
                    </div>
                </div>
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: "Salvar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#2f9e44",
            cancelButtonColor: "#6c757d",
            preConfirm: () => {
                const name = document.getElementById("run-save-name").value.trim();
                if (!name) {
                    Swal.showValidationMessage("O nome da sessão é obrigatório.");
                    return false;
                }

                return {
                    name,
                    group: document.getElementById("run-save-group").value,
                    objective: document.getElementById("run-save-objective").value.trim(),
                    level: document.getElementById("run-save-level").value.trim(),
                    estimatedDistanceKm: document.getElementById("run-save-distance").value.trim(),
                    estimatedDurationMin: document.getElementById("run-save-duration").value.trim(),
                    tags: document.getElementById("run-save-tags").value.split(",").map(tag => tag.trim()).filter(Boolean),
                    coachNotes: document.getElementById("run-save-notes").value.trim(),
                };
            },
        });

        if (!formValues) return;

        try {
            await RunningSessionLibraryService.save({
                idTenant,
                authUser,
                session: {
                    ...formValues,
                    sessionType: workout.blocks?.[0]?.sessionType || "easy",
                    locationType: workout.blocks?.[0]?.locationType || "street",
                    terrain: workout.blocks?.[0]?.terrain || "flat",
                    clubSession: workout.clubSession || null,
                    cyclePlan: workout.cyclePlan || null,
                    attendancePlan: workout.attendancePlan || null,
                    blocks: workout.blocks || [],
                },
            });
            setLibraryRefreshKey(prev => prev + 1);
            toast.success("Sessão completa salva na biblioteca.");
        } catch (error) {
            toast.error(error.message || "Erro ao salvar sessão completa.");
        }
    };

    const handleExportRunningWatchWorkout = async (workout) => {
        const paceGroups = (workout.clubSession?.paceGroups || []).filter(group => group.enabled !== false);
        const hasClubGroups = workout.clubSession?.isClubSession && paceGroups.length > 0;

        const { value: selectedGroupIndex } = await Swal.fire({
            title: "Exportar para relógio",
            width: 560,
            html: `
                <div class="text-start">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Versão do treino</label>
                    <select id="run-watch-group" class="form-control">
                        <option value="solo">Sem grupo específico</option>
                        ${hasClubGroups ? paceGroups.map((group, index) => `
                            <option value="${index}">${group.label || `Grupo ${index + 1}`} - ${group.desc || "Grupo de pace"}</option>
                        `).join("") : ""}
                    </select>
                    <small class="text-muted d-block mt-2">
                        A exportação gera um JSON estruturado. No futuro esse arquivo será convertido para Garmin/FIT/TCX ou enviado por integração.
                    </small>
                </div>
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: "Exportar JSON",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#2f9e44",
            cancelButtonColor: "#6c757d",
            preConfirm: () => document.getElementById("run-watch-group").value,
        });

        if (!selectedGroupIndex) return;

        const groupIndex = selectedGroupIndex === "solo" ? -1 : Number(selectedGroupIndex);
        const paceGroup = groupIndex >= 0 ? paceGroups[groupIndex] : null;
        const payload = buildRunningWatchExport({ workout, paceGroup, groupIndex });

        downloadRunningWatchExport(payload);
        toast.success("Arquivo de exportação gerado.");
    };

    // ── Callback genérico para abrir a biblioteca pela modalidade ──
    const handleOpenLibrary = (modality) => {
        switch (modality) {
            case "swimming":  setLibrarySidebarOpen(true); break;
            case "strength":  setStrengthLibrarySidebarOpen(true); break;
            case "wod":       setWodLibrarySidebarOpen(true); break;
            case "running":   setRunningLibrarySidebarOpen(true); break;
            default: break;
        }
    };

    // ── Offcanvas helper: "Bloco de Destino" selector ──
    const renderBlockDestinationBox = (variant) => (
        <div className={`block-destination-box ${variant}`}>
            <label className="form-label font-size-11 fw-bold text-uppercase text-muted mb-2 d-block">
                Bloco de Destino
            </label>
            <NeoInput
                type="select"
                size="sm"
                className="swim-session-block-target w-100"
                value={targetBlockBySession[activeTab] ?? 0}
                onChange={(event) => setTargetBlockBySession(prev => ({
                    ...prev,
                    [activeTab]: Number(event.target.value),
                }))}
            >
                {((workouts[activeTab] || {}).blocks || []).map((block, blockIndex) => (
                    <option key={block.id || blockIndex} value={blockIndex}>
                        {block.name || `Bloco ${blockIndex + 1}`}
                    </option>
                ))}
            </NeoInput>
            <small className="text-muted font-size-10 mt-2 d-block">
                {variant === "swimming"
                    ? "O set selecionado abaixo será inserido no fim do bloco escolhido acima."
                    : "O exercício selecionado abaixo será inserido no fim do bloco escolhido acima."}
            </small>
        </div>
    );

    // ── Offcanvas helper: header com ícone ──
    const renderOffcanvasTitle = (icon, iconBg, iconColor, title, subtitle) => (
        <div className="d-flex align-items-center">
            <div className={`modal-icon-bg ${iconBg} me-3 animate__animated animate__zoomIn`}>
                <i className={`mdi ${icon} ${iconColor} font-size-22`} />
            </div>
            <div>
                <span className="fw-bold text-brand-primary font-size-15 d-block neo-font-outfit">
                    {title}
                </span>
                <small className="text-muted font-size-11 fw-normal d-block mt-0.5 neo-font-outfit">
                    {subtitle}
                </small>
            </div>
        </div>
    );

    return (
        <div>
            {showTabs && (
                <NeoTabs
                    tabs={workouts.map((workout, index) => ({ id: index, label: workout.name }))}
                    activeTab={activeTab}
                    onChangeTab={setActiveTab}
                    className="mb-4"
                />
            )}

            <TabContent activeTab={activeTab}>
                {workouts.map((workout, index) => (
                    <TabPane tabId={index} key={workout.id || index}>
                        <WorkoutSessionPanel
                            workout={workout}
                            index={index}
                            defaultWorkoutType={defaultWorkoutType}
                            workouts={workouts}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            onLibraryChanged={() => setLibraryRefreshKey(prev => prev + 1)}
                            runningConfigTab={runningConfigTab}
                            setRunningConfigTab={setRunningConfigTab}
                            runningCoachBriefOpen={runningCoachBriefOpen}
                            setRunningCoachBriefOpen={setRunningCoachBriefOpen}
                            onOpenLibrary={handleOpenLibrary}
                            onOpenRunningHelp={() => setRunningHelpSidebarOpen(true)}
                            onSaveRunningSession={handleSaveRunningWorkoutSession}
                            onExportRunningWatch={handleExportRunningWatchWorkout}
                        />
                    </TabPane>
                ))}
            </TabContent>

            {/* ── Offcanvas: Biblioteca de Sets (Natação) ── */}
            {isSwimming && (
                <Offcanvas
                    isOpen={librarySidebarOpen}
                    toggle={() => setLibrarySidebarOpen(false)}
                    direction="end"
                    className="workout-offcanvas-swimming neo-font-outfit"
                >
                    <OffcanvasHeader toggle={() => setLibrarySidebarOpen(false)} className="pb-3 border-bottom-0">
                        {renderOffcanvasTitle("mdi-book-open-variant", "bg-primary-soft", "text-brand-primary", "Biblioteca de Sets", "Escolha os sets para adicionar diretamente à sessão de treino.")}
                    </OffcanvasHeader>
                    <OffcanvasBody className="p-4 bg-white d-flex flex-column h-100 pt-0">
                        {renderBlockDestinationBox("swimming")}
                        <div className="flex-grow-1">
                            <SwimSetLibraryPicker
                                refreshKey={libraryRefreshKey}
                                onAddSet={(set) => {
                                    handleAddSwimSet(activeTab, set);
                                    toast.success("Set adicionado com sucesso!");
                                }}
                            />
                        </div>
                    </OffcanvasBody>
                </Offcanvas>
            )}

            {/* ── Offcanvas: Biblioteca de Exercícios (Musculação) ── */}
            {isStrength && (
                <Offcanvas
                    isOpen={strengthLibrarySidebarOpen}
                    toggle={() => setStrengthLibrarySidebarOpen(false)}
                    direction="end"
                    className="workout-offcanvas-strength neo-font-outfit"
                >
                    <OffcanvasHeader toggle={() => setStrengthLibrarySidebarOpen(false)} className="pb-3 border-bottom-0">
                        {renderOffcanvasTitle("mdi-dumbbell", "bg-warning-soft", "text-warning", "Biblioteca de Exercícios", "Escolha os exercícios para adicionar diretamente à sessão de treino.")}
                    </OffcanvasHeader>
                    <OffcanvasBody className="p-4 bg-white d-flex flex-column h-100 pt-0">
                        {renderBlockDestinationBox("strength")}
                        <div className="flex-grow-1">
                            <StrengthExerciseLibraryPicker
                                refreshKey={libraryRefreshKey}
                                onAddExercise={(exercise) => {
                                    handleAddStrengthExercise(activeTab, exercise);
                                    toast.success("Exercício adicionado com sucesso!");
                                }}
                            />
                        </div>
                    </OffcanvasBody>
                </Offcanvas>
            )}

            {/* ── Offcanvas: Biblioteca de WODs ── */}
            {isWod && (
                <Offcanvas
                    isOpen={wodLibrarySidebarOpen}
                    toggle={() => setWodLibrarySidebarOpen(false)}
                    direction="end"
                    className="workout-offcanvas-wod neo-font-outfit"
                >
                    <OffcanvasHeader toggle={() => setWodLibrarySidebarOpen(false)} className="pb-3 border-bottom-0">
                        {renderOffcanvasTitle("mdi-lightning-bolt", "bg-warning-soft", "text-warning", "Biblioteca de WODs", "Escolha um benchmark para substituir a sessão atual.")}
                    </OffcanvasHeader>
                    <OffcanvasBody className="p-4 bg-white d-flex flex-column h-100 pt-0">
                        <WodBenchmarkLibraryPicker
                            refreshKey={libraryRefreshKey}
                            onAddBenchmark={(benchmark) => {
                                handleAddWodBenchmark(activeTab, benchmark);
                                toast.success("WOD adicionado à sessão!");
                            }}
                        />
                    </OffcanvasBody>
                </Offcanvas>
            )}

            {/* ── Offcanvas: Biblioteca de Sessões de Corrida ── */}
            {isRunning && (
                <Offcanvas
                    isOpen={runningLibrarySidebarOpen}
                    toggle={() => setRunningLibrarySidebarOpen(false)}
                    direction="end"
                    className="workout-offcanvas-running neo-font-outfit"
                >
                    <OffcanvasHeader toggle={() => setRunningLibrarySidebarOpen(false)} className="pb-3 border-bottom-0">
                        {renderOffcanvasTitle("mdi-run-fast", "bg-success-soft", "text-success", "Biblioteca de Sessões", "Escolha uma sessão estruturada para substituir a sessão atual.")}
                    </OffcanvasHeader>
                    <OffcanvasBody className="p-4 bg-white d-flex flex-column h-100 pt-0">
                        <RunningSessionLibraryPicker
                            refreshKey={libraryRefreshKey}
                            onAddSession={(session) => {
                                handleAddRunningSession(activeTab, session);
                                toast.success("Sessão de corrida adicionada!");
                            }}
                        />
                    </OffcanvasBody>
                </Offcanvas>
            )}

            {/* ── Offcanvas: Guia de Corrida ── */}
            {isRunning && (
                <Offcanvas
                    isOpen={runningHelpSidebarOpen}
                    toggle={() => setRunningHelpSidebarOpen(false)}
                    direction="end"
                    className="workout-offcanvas-running-help neo-font-outfit"
                >
                    <OffcanvasHeader toggle={() => setRunningHelpSidebarOpen(false)} className="pb-3 border-bottom-0">
                        {renderOffcanvasTitle("mdi-help-circle-outline", "bg-success-soft", "text-success", "Guia de Corrida", "Entenda os campos e o melhor fluxo para criar treinos.")}
                    </OffcanvasHeader>
                    <OffcanvasBody className="p-4 bg-white pt-0">
                        <RunningHelpPanel />
                    </OffcanvasBody>
                </Offcanvas>
            )}
        </div>
    );
};

export default WorkoutTabsEditor;
